import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  ChevronDown,
  ChevronUp,
  Folder,
  HealthInHand,
  HouseMedical,
  ShieldHeart,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { IconUsageProps } from '@insurancemenu/imenu-components-lib/dist/types';
import { IMBaseColors } from '@insurancemenu/imenu-components-lib/dist/utils/colors';
import { ProductLineSectionType } from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import AddQuoteProductLineHeader from 'components/quote/add-quote/AddQuoteProductLineSection/AddQuoteProductLineHeader';
import AddQuoteCartItems from 'components/quote/add-quote/AddQuoteProductLineSection/AddQuoteCartItems';
import { ProductLineDelegateContext } from 'store/pages/proposal/productLineContext';
import ProductLineDelegate from 'store/modules/proposal/delegates/productLineDelegate';
import { useAddQuotePageStore } from 'store/pages/quote/add-quote/pageStore';
import { Collapse, colors } from '@insurancemenu/imenu-components-lib';
import { ProductLineSectionGroup } from 'store/pages/quote/add-quote/types';
import { ADD_QUOTE_TEST_IDS } from '../constants';
import {
  EmptyStateLabelStyled,
  EmptyStateStyled,
  IconWrapperStyled,
  ProductLineBlockStyled,
  ProductLineSectionStyled,
  SectionHeaderStyled,
  SectionInfoStyled,
  SectionInfoWrapperStyled,
  SectionTitleStyled,
  SectionTitleSubtitleStyled,
  ToggleButtonStyled,
} from './styles';

interface ProductLineProps {
  productLineId: ProductLineId;
}

interface ProductLineSectionProps {
  sectionGroup: ProductLineSectionGroup;
}

interface SectionInfo {
  label: string;
  Icon: React.FC<IconUsageProps>;
  accentColor: IMBaseColors;
  size: string;
}

const PRODUCT_LINE_SECTION_MAP: Record<ProductLineSectionType, SectionInfo> = {
  [ProductLineSectionType.GENERAL]: { label: 'General', Icon: HouseMedical, accentColor: 'blue', size: '3rem' },
  [ProductLineSectionType.LIFE_AND_DISABILITY]: {
    label: 'Life & Disability',
    Icon: ShieldHeart,
    accentColor: 'yellow',
    size: '3.4rem',
  },
  [ProductLineSectionType.SUPPLEMENTARY_HEALTH]: {
    label: 'Supplementary Health',
    Icon: HealthInHand,
    accentColor: 'purple',
    size: '3.2rem',
  },
  [ProductLineSectionType.ANCILLARY]: {
    label: 'Ancillary',
    Icon: Folder,
    accentColor: 'neutral',
    size: '2.8rem',
  },
};

const EmptyProductLine: React.FC<ProductLineProps> = observer(({ productLineId }) => {
  return (
    <ProductLineBlockStyled>
      <AddQuoteProductLineHeader productLineId={productLineId} />
      <EmptyStateStyled data-test-id={ADD_QUOTE_TEST_IDS.EMPTY_STATE}>
        <EmptyStateLabelStyled>No plans added yet</EmptyStateLabelStyled>
      </EmptyStateStyled>
    </ProductLineBlockStyled>
  );
});

const ProductLineWithPlans: React.FC<ProductLineProps> = observer(({ productLineId }) => {
  const { proposalPageStore } = useAddQuotePageStore();

  return (
    <ProductLineBlockStyled>
      <ProductLineDelegateContext.Provider value={new ProductLineDelegate(proposalPageStore, productLineId)}>
        <AddQuoteProductLineHeader productLineId={productLineId} />
        <AddQuoteCartItems productLineId={productLineId} />
      </ProductLineDelegateContext.Provider>
    </ProductLineBlockStyled>
  );
});

const ProductLineSection: React.FC<ProductLineSectionProps> = observer(({ sectionGroup }) => {
  const { section: sectionType, productLineIds, productLineIdsWithPlans } = sectionGroup;
  const { label, Icon, accentColor, size } = PRODUCT_LINE_SECTION_MAP[sectionType];

  const [isOpen, setIsOpen] = useState(
    sectionType === ProductLineSectionType.GENERAL || productLineIdsWithPlans.length > 0,
  );

  return (
    <ProductLineSectionStyled data-test-id={ADD_QUOTE_TEST_IDS.SECTION}>
      <SectionHeaderStyled>
        <SectionInfoWrapperStyled>
          <IconWrapperStyled $accentColor={colors[accentColor][100]}>
            <Icon width={size} height={size} color={colors[accentColor][600]} />
          </IconWrapperStyled>
          <SectionInfoStyled>
            <SectionTitleStyled>{label}</SectionTitleStyled>
            <SectionTitleSubtitleStyled>
              {productLineIdsWithPlans.length}/{productLineIds.length} product lines have plans
            </SectionTitleSubtitleStyled>
          </SectionInfoStyled>
        </SectionInfoWrapperStyled>
        <ToggleButtonStyled
          type="text"
          size="small"
          color="primary"
          uppercase
          onClick={() => setIsOpen((current) => !current)}
          rightIconProp={isOpen ? <ChevronUp /> : <ChevronDown />}
          data-test-id={ADD_QUOTE_TEST_IDS.SECTION_TOGGLE}
        >
          {isOpen ? 'Hide Product Lines' : 'Show Product Lines'}
        </ToggleButtonStyled>
      </SectionHeaderStyled>

      <Collapse show={isOpen}>
        {productLineIds.map((productLineId) =>
          productLineIdsWithPlans.includes(productLineId) ? (
            <ProductLineWithPlans key={productLineId} productLineId={productLineId} />
          ) : (
            <EmptyProductLine key={productLineId} productLineId={productLineId} />
          ),
        )}
      </Collapse>
    </ProductLineSectionStyled>
  );
});

export default ProductLineSection;
